// Brochure-accurate benefit tables for the Coverage Summary PDF.
//
// Each template carries:
//   id           — stable slug used in coverage_summaries.plans[].templateId
//   name         — full plan name shown in big type
//   short        — short label for chips ("Guard 6000")
//   carrier      — full carrier line under the plan name
//   category     — chip color category (matches PDF: foundation/accident/CI/dental/vision)
//   tagline      — single-line tagline shown under the plan name
//   accent       — color used for the page banner + chip ('blue','red','teal','violet','green','sky','amber')
//   highlightKpi — { label, value } shown big in the snapshot page
//   rows         — array of { left: {label, sub?, value}, right: {label, sub?, value} } pairs
//   footnote     — single string under the table
//
// Categories are deliberately *opinionated* — these are the plans
// FTPIG agents actually write, not the entire UHC catalog.

export const COVERAGE_TEMPLATES = [
  {
    id: 'guard_6000',
    name: 'Guard 6000',
    short: 'Guard 6000',
    carrier: 'UnitedHealthcare · Golden Rule Insurance Co.',
    category: 'foundation',
    tagline: 'Fixed-benefit hospital & medical · $0 deductible',
    accent: 'blue',
    badge: 'Pays cash to you',
    highlightKpi: { label: 'Hospital admit', value: '$6,000' },
    intro: 'This is the workhorse of your package. When you\'re hospitalized, have surgery, visit the ER, or see a doctor, Guard 6000 pays a fixed cash benefit — no deductible to meet first. Several benefits even double in year two just for keeping the plan.',
    rows: [
      { left: { label: 'Hospital admission', sub: '(first day)',         value: '$6,000' },
        right:{ label: 'Surgery',            sub: '(by procedure tier)', value: 'up to $25,000' } },
      { left: { label: 'Daily hospital stay', sub: '(up to 31 days)',    value: '$100 → $200 yr 2' },
        right:{ label: 'Outpatient surgery facility', sub: '',           value: '$2,000/day' } },
      { left: { label: 'Inpatient doctor visit', sub: '',                value: '$100/day' },
        right:{ label: 'Imaging',            sub: '(MRI / CT / PET)',    value: '$500/test' } },
      { left: { label: 'Emergency room',    sub: '(up to 3 days)',       value: '$600/day' },
        right:{ label: 'Doctor, urgent care & Rx', sub: '',              value: 'Included' } },
      { left: { label: 'Ambulance',         sub: '(ground / air)',       value: '$600 / $3,000' },
        right:{ label: 'Virtual care',      sub: '(Teladoc)',            value: '$0' } },
    ],
    callout: {
      title: 'How surgery pays',
      body: 'Surgery is paid on a 7-tier schedule based on the procedure — from $250 for minor procedures up to $25,000 for a major organ transplant. Examples: a knee or hip replacement pays $1,250; a procedure like a colectomy pays $2,500. Your surgeon\'s anesthesiologist and assistant add 30% and 20% on top.',
    },
    footnote: 'Use any provider you like. When you use a UnitedHealthcare Choice Plus network provider, your bill is discounted to the contracted rate first, then your fixed benefit is applied — which often means little or nothing left to pay out of pocket. Benefits are subject to a 12-month look-back on pre-existing conditions.',
  },

  {
    id: 'accidentwise_15000',
    name: 'AccidentWise 15000',
    short: 'AccidentWise 15000',
    carrier: 'UnitedHealthcare · Golden Rule Insurance Co.',
    category: 'accident',
    tagline: 'Cash for accidental injuries · pays on top of everything else',
    accent: 'red',
    badge: 'No health questions',
    highlightKpi: { label: 'Injury admit', value: '$15,000' },
    intro: 'Trip, fall, or break something? AccidentWise pays a lump-sum cash benefit for accident-related care — on top of any other coverage you have. Guaranteed issue: no medical questions asked.',
    rows: [
      { left: { label: 'Hospital admission', sub: '(from injury)',  value: '$15,000' },
        right:{ label: 'Major diagnostic',  sub: '(CT / MRI / EEG)', value: '$1,500' } },
      { left: { label: 'Emergency room',    sub: '(within 72 hrs)',  value: '$1,500' },
        right:{ label: 'Follow-up care / physical therapy', sub: '', value: '$150/visit' } },
      { left: { label: 'Urgent care',       sub: '(within 72 hrs)',  value: '$300' },
        right:{ label: 'Accidental loss of life, limb, sight', sub: '', value: '$15,000' } },
      { left: { label: 'Outpatient surgery', sub: '',                value: '$1,500' },
        right:{ label: 'Premiums rise with age?', sub: '',            value: 'Never' } },
    ],
    footnote: 'No health questions, no waiting period. Spouse 16-75 and eligible children 0-25 may be added.',
  },

  {
    id: 'criticalguard_combo',
    name: 'CriticalGuard — Cancer + Heart/Stroke',
    short: 'CriticalGuard',
    carrier: 'UnitedHealthcare · Golden Rule Insurance Co.',
    category: 'critical_illness',
    tagline: 'Lump-sum cash on first diagnosis',
    accent: 'teal',
    badge: 'Lump-sum',
    highlightKpi: { label: 'Lump-sum benefit', value: 'You pick' }, // populated by row.customAmount
    intro: 'A single check on first diagnosis. Use it for treatment, bills, lost income, or anything else. Pays in addition to any other insurance you may have.',
    rows: [
      { left: { label: 'Life-threatening cancer', sub: '',  value: '100% of benefit' },
        right:{ label: 'Cancer in situ / benign brain tumor', sub: '', value: '25%' } },
      { left: { label: 'Heart attack',            sub: '',  value: '100% of benefit' },
        right:{ label: 'Major heart procedures',  sub: '',  value: '25%' } },
      { left: { label: 'Stroke',                  sub: '',  value: '100% of benefit' },
        right:{ label: 'COVID ICU confinement',   sub: '',  value: '$10,000' } },
    ],
    callout: {
      title: 'Also included — at no extra cost',
      body: 'Cancer Resource Services (a dedicated oncology nurse · second opinions · Centers of Excellence access). Heart & Coronary Care Program (personal coaching to manage heart conditions and reduce hospitalizations). Optum Perks (Rx discounts of 30-80% — perks.optum.com/uho).',
    },
    footnote: 'Plus $500 paid for skin cancer. 30-day waiting period from your start date. Benefit lifetime max is what you chose at enrollment.',
  },

  {
    id: 'dentalwise_2000',
    name: 'DentalWise Plan 2000',
    short: 'DentalWise 2000',
    carrier: 'UnitedHealthcare · Golden Rule Insurance Co.',
    category: 'dental',
    tagline: 'Dental coverage · up to $2,000 per year',
    accent: 'violet',
    badge: '$2,000 annual max',
    highlightKpi: { label: 'Annual max', value: '$2,000' },
    intro: 'Cleanings, fillings, and most major work all start day one — no waiting period. Implants kick in at month 12.',
    rows: [
      { left: { label: 'Cleanings & exams', sub: '(2/yr)', value: '100% covered' },
        right:{ label: 'Crowns, root canals, dentures', sub: '', value: '15% → 50%' } },
      { left: { label: 'X-rays', sub: '', value: '100% covered' },
        right:{ label: 'Implants', sub: '(12-mo wait)', value: '50% · $1,500' } },
      { left: { label: 'Fillings', sub: '(basic)', value: '60% → 80%' },
        right:{ label: 'Annual deductible', sub: '', value: '$100' } },
    ],
    footnote: 'No waiting period for cleanings, fillings, or major work (implants excepted). Coverage on bigger procedures grows in year two — fillings rise to 80% and crowns/root canals to 50%.',
  },

  {
    id: 'visionwise_premier',
    name: 'VisionWise Premier',
    short: 'VisionWise Premier',
    carrier: 'UnitedHealthcare Vision · administered by Spectera',
    category: 'vision',
    tagline: 'Vision coverage · UnitedHealthcare Vision network',
    accent: 'green',
    badge: 'No waiting period',
    highlightKpi: { label: 'Annual eye exam', value: '$0' },
    intro: 'Free annual eye exam, plus generous allowances toward frames, lenses, or contacts. No claim forms needed in network.',
    rows: [
      { left: { label: 'Annual eye exam', sub: '(in network)', value: '$0' },
        right:{ label: 'Lenses', sub: '(single / bi / trifocal)', value: '$10 copay' } },
      { left: { label: 'Frames allowance', sub: '', value: 'up to $150' },
        right:{ label: 'Contacts allowance', sub: '(in lieu of glasses)', value: 'up to $150' } },
    ],
    footnote: 'Benefits available once per plan year. Find network eye doctors and retail providers at myuhcvision.com — no ID card or claim forms needed in network. Exact Premier-tier allowances are confirmed in your policy documents.',
  },

  {
    id: 'hospital_safeguard_2000',
    name: 'Hospital SafeGuard 2000',
    short: 'Hospital SafeGuard',
    carrier: 'UnitedHealthcare · Golden Rule Insurance Co.',
    category: 'foundation',
    tagline: 'Guaranteed-issue hospital indemnity · $2,000/day',
    accent: 'sky',
    badge: 'GI — no medical questions',
    highlightKpi: { label: 'Daily hospital', value: '$2,000' },
    intro: 'Guaranteed-issue hospital indemnity. No medical underwriting. Pays a fixed daily benefit for hospital confinement.',
    rows: [
      { left: { label: 'Daily hospital confinement', sub: '', value: 'up to $2,000/day' },
        right:{ label: 'Accidental loss rider',      sub: '', value: 'Included' } },
      { left: { label: 'Issue ages',                 sub: '', value: '18-64' },
        right:{ label: 'Pre-existing lookback',       sub: '', value: '12 months' } },
    ],
    footnote: '18-64 (DE + MD: 18-60). GI · no medical underwriting. Includes accidental loss of life/limb/sight rider.',
  },

  {
    id: 'teladoc_health',
    name: 'Teladoc Health (HealthiestYou)',
    short: 'Teladoc',
    carrier: 'Teladoc Health',
    category: 'telemedicine',
    tagline: 'Unlimited 24/7 telehealth · $0 visits',
    accent: 'amber',
    badge: 'NOT INSURANCE',
    highlightKpi: { label: 'Visit cost', value: '$0' },
    intro: '24/7 access to a real doctor by phone, video, or app. General medical, mental health, and dermatology — for everyone on the plan.',
    rows: [
      { left: { label: 'General medical', sub: '', value: '$0' },
        right:{ label: 'Mental health',   sub: '(behavioral)', value: 'Add-on fee' } },
      { left: { label: 'Dermatology',     sub: '', value: 'Add-on fee' },
        right:{ label: 'Family included', sub: '', value: 'Yes' } },
    ],
    footnote: 'NOT INSURANCE. Telehealth membership only. Does not replace your medical insurance.',
  },
];

export const TEMPLATE_BY_ID = Object.fromEntries(COVERAGE_TEMPLATES.map(t => [t.id, t]));

// Real-dollar example scenarios — shown on the "What your package
// actually pays" page. Each scenario lists which template ids the
// math expects to be in the package; the PDF dynamically skips
// scenarios that don't apply.
export const COVERAGE_SCENARIOS = [
  {
    id: 'break_ankle',
    icon: '🦴',
    headline: 'You slip and break your ankle — ER visit, outpatient surgery, and follow-up PT',
    requires: ['accidentwise_15000'],
    lines: [
      { label: 'AccidentWise — Emergency room',                 value: 1500 },
      { label: 'AccidentWise — Outpatient surgery',             value: 1500 },
      { label: 'AccidentWise — Diagnostic imaging (X-ray/MRI)', value: 1500 },
      { label: 'AccidentWise — Follow-up PT (5 visits × $150)', value: 750  },
    ],
    totalLabel: 'Cash paid directly to you',
  },
  {
    id: 'hospital_pneumonia',
    icon: '🏥',
    headline: 'A 3-day hospital stay for pneumonia (an illness, not an accident)',
    requires: ['guard_6000'],
    lines: [
      { label: 'Guard 6000 — Hospital admission (first day)',   value: 6000 },
      { label: 'Guard 6000 — Daily stay (3 days × $100)',       value: 300  },
      { label: 'Guard 6000 — Inpatient doctor visits',          value: 300  },
      { label: 'AccidentWise — does not apply (illness)',       value: 0    },
    ],
    totalLabel: 'Cash paid directly to you',
  },
  {
    id: 'cancer_diagnosis',
    icon: '❤️',
    headline: 'You\'re diagnosed with cancer and begin treatment',
    requires: ['criticalguard_combo'],
    lines: [
      { label: 'CriticalGuard — lump sum on first diagnosis',   value: null, mark: '+ full benefit' },
      { label: 'Guard 6000 — hospital & surgery cash during treatment', value: null, mark: '+ as incurred' },
    ],
    totalLabel: 'Cash to focus on recovery, not bills',
    totalMark: '$___ +',
  },
  {
    id: 'dental_implant',
    icon: '🦷',
    headline: 'You need a crown after a year on the plan',
    requires: ['dentalwise_2000'],
    lines: [
      { label: 'DentalWise — Crown (50% after year 1)',         value: 850  },
      { label: 'DentalWise — Exam + X-rays at the visit',       value: 175  },
    ],
    totalLabel: 'Out of your pocket: roughly half',
  },
  {
    id: 'eye_glasses',
    icon: '👓',
    headline: 'Annual eye exam + new glasses',
    requires: ['visionwise_premier'],
    lines: [
      { label: 'VisionWise — Annual eye exam (in network)',     value: 0    },
      { label: 'VisionWise — Frames allowance',                 value: 150  },
      { label: 'VisionWise — Single-vision lenses ($10 copay)', value: -10  },
    ],
    totalLabel: 'Bottom line on a routine visit',
  },
];

// Provider quick-link generator. Returns an ordered list of cards
// keyed by what an agent's client typically needs. The deeplinks
// Google-Maps a "X near ZIP" search — universal, no API key, works
// the same in every browser. The UHC network finders accept no ZIP
// parameter reliably, so we surface them as raw entry points.
export function providerLinks({ zip = '', state = '' }) {
  const q = (s) => encodeURIComponent(`${s}${zip ? ` near ${zip}` : ''}`);
  const maps = (s) => `https://www.google.com/maps/search/?api=1&query=${q(s)}`;

  return [
    { id: 'pcp',         icon: '🩺', label: 'Primary care doctor', sub: 'UnitedHealthcare Choice Plus network',
      primaryUrl: 'https://www.uhc.com/find-a-provider',          primaryHost: 'uhc.com',
      mapsUrl: maps('primary care doctor') },
    { id: 'urgent_care', icon: '🚑', label: 'Urgent care',         sub: 'Walk-in, 7-day care',
      primaryUrl: maps('urgent care'),                            primaryHost: 'google.com/maps',
      mapsUrl: maps('urgent care') },
    { id: 'hospital',    icon: '🏥', label: 'Hospital / ER',       sub: 'Emergency room locations',
      primaryUrl: maps('hospital emergency room'),                primaryHost: 'google.com/maps',
      mapsUrl: maps('hospital emergency room') },
    { id: 'dentist',     icon: '🦷', label: 'Dentist',             sub: 'Dental Benefit Providers network',
      primaryUrl: 'https://www.uhcdental.com',                    primaryHost: 'uhcdental.com',
      mapsUrl: maps('dentist') },
    { id: 'eye',         icon: '👓', label: 'Eye doctor',          sub: 'UHC Vision · Spectera',
      primaryUrl: 'https://www.myuhcvision.com',                  primaryHost: 'myuhcvision.com',
      mapsUrl: maps('eye doctor optometrist') },
    { id: 'mental',      icon: '🧠', label: 'Mental health',       sub: 'Therapist / counselor',
      primaryUrl: `https://www.psychologytoday.com/us/therapists${zip ? `/${zip}` : ''}`,
      primaryHost: 'psychologytoday.com',
      mapsUrl: maps('therapist counselor') },
    { id: 'pharmacy',    icon: '💊', label: 'Pharmacy',            sub: 'Optum Perks Rx discount',
      primaryUrl: 'https://perks.optum.com/uho',                  primaryHost: 'perks.optum.com',
      mapsUrl: maps('pharmacy') },
    { id: 'claims',      icon: '📋', label: 'File a claim',        sub: 'UHC Member Hub',
      primaryUrl: 'https://www.uhcmemberhub.com',                 primaryHost: 'uhcmemberhub.com',
      mapsUrl: null },
  ];
}
